import React from "react";
import { colors, gradients } from "./theme";

// Small presentational pieces shared by the V2 pages. The hexagons are drawn as
// CSS clip-path shapes so they scale and print without an image asset.

// Outlined hexagon holding a statistic ("20+ years of experience").
//
// The outline is the supplied artwork, which has a gap on its left edge - the
// number sits in that gap and the label inside the shape. Applied as the
// background so the shape keeps its aspect ratio rather than being stretched to
// the box.
export const V2HexStat = ({ value, label, outline }) => (
  <div
    className="report-v2__hex-stat"
    style={{ backgroundImage: `url(${outline})` }}
  >
    <strong className="report-v2__hex-stat-value">{value}</strong>
    <span className="report-v2__hex-stat-label">{label}</span>
  </div>
);

// Outlined hexagon with a title and body, used for the SELECTIO services page.
//
// The outline is the supplied hex-white.svg, applied as the card's background
// rather than as an <img>, so the shape always covers the card exactly and the
// text sits on top of it without needing its own stacking context.
export const V2HexCard = ({ title, children, outline }) => (
  <div
    className="report-v2__hex-card"
    style={{ backgroundImage: `url(${outline})` }}
  >
    <div className="report-v2__hex-card-text">
      <h4>{title}</h4>
      <p>{children}</p>
    </div>
  </div>
);

// Rounded gradient card - development activities, certifier list, timeline.
export const V2Card = ({ title, subtitle, children, className }) => (
  <div
    className={`report-v2__card${className ? ` ${className}` : ""}`}
    style={{ background: gradients.card }}
  >
    {title && <strong>{title}</strong>}
    {subtitle && <span>{subtitle}</span>}
    {children}
  </div>
);

// Numbered horizontal timeline (Employer Brand Visibility, Timeline).
export const V2Timeline = ({ steps, showCards }) => (
  <div className="report-v2__timeline">
    <div className="report-v2__timeline-track">
      {steps.map((step, key) => (
        <div className="report-v2__timeline-node" key={key}>
          <span
            className="report-v2__timeline-dot"
            style={{
              background: showCards ? colors.secondary : "#FFFFFF",
              borderColor: colors.secondary,
            }}
          >
            {!showCards && key + 1}
          </span>
          {step.date && (
            <span className="report-v2__timeline-date">{step.date}</span>
          )}
        </div>
      ))}
    </div>

    <div className="report-v2__timeline-items">
      {steps.map((step, key) =>
        showCards ? (
          <div className="report-v2__timeline-item" key={key}>
            <span className="report-v2__timeline-index">{key + 1}</span>
            <V2Card>{step.title}</V2Card>
          </div>
        ) : (
          <div className="report-v2__timeline-item" key={key}>
            <strong>{step.title}</strong>
            <p>{step.text}</p>
          </div>
        ),
      )}
    </div>
  </div>
);

// Status chips on the cover ("STATUS: CERTIFIED", "YEAR: 2026").
// Renders as a link when `href` is given, so the printed PDF keeps a clickable
// call to action, and as a plain span otherwise.
export const V2Chip = ({ children, href }) =>
  href ? (
    <a
      className="report-v2__chip"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ) : (
    <span className="report-v2__chip">{children}</span>
  );

// The SELECTIO + Employer Partner logo lockup used on the dark slides.
export const V2Lockup = ({ selectio, employerPartner }) => (
  <div className="report-v2__lockup">
    <img className="report-v2__lockup-selectio" src={selectio} alt="SELECTIO" />
    <img
      className="report-v2__lockup-ep"
      src={employerPartner}
      alt="Employer Partner"
    />
  </div>
);
